import {
  durationMs,
  type ReplayIdentity,
  type ReplayTiming,
  type ReplayOutcome,
} from "./replay-timing";

type ReplayTimingContext = Readonly<
  Record<string, string | number | boolean | null | undefined>
>;

export function replayTimingContext({
  current,
  endedAt,
  outcome,
  failureStage,
  measurementKind,
  coworldId,
  telemetryContext,
  surface,
}: ReplayIdentity &
  Readonly<{
    current: Readonly<ReplayTiming>;
    endedAt: number;
    outcome: ReplayOutcome;
    failureStage: string | null;
    measurementKind: "iframe_load_double_raf" | "viewer_ready_message";
  }>): ReplayTimingContext {
  const succeeded = outcome === "success";
  const totalMs = durationMs({ start: current.requestStartedAt, end: endedAt });
  const hiddenMs =
    current.hiddenMs +
    (current.hiddenSince === null ? 0 : endedAt - current.hiddenSince);
  return {
    replay_request_to_approx_first_paint_ms: succeeded ? totalMs : undefined,
    session_create_ms: durationMs({
      start: current.requestStartedAt,
      end: current.responseReceivedAt,
    }),
    runtime_ready_ms: durationMs({
      start: current.responseReceivedAt,
      end: current.runtimeReadyAt,
    }),
    iframe_document_load_ms: durationMs({
      start: current.iframeInsertedAt,
      end: current.iframeLoadedAt,
    }),
    approx_first_paint_ms: succeeded
      ? durationMs({ start: current.iframeInsertedAt, end: endedAt })
      : undefined,
    bundle_ready_ms: durationMs({
      start: current.iframeInsertedAt,
      end: current.bundleReadyAt,
    }),
    replay_fetch_ms: durationMs({
      start: current.replayFetchStartAt,
      end: current.replayFetchEndAt,
    }),
    replay_parse_ms: durationMs({
      start: current.replayFetchEndAt,
      end: current.replayParsedAt,
    }),
    replay_draw_ms: succeeded
      ? durationMs({ start: current.replayParsedAt, end: endedAt })
      : undefined,
    replay_bytes: current.replayBytes,
    replay_compressed: current.replayCompressed,
    viewer_manifest_hash: current.viewerManifestHash,
    page_visible_at_end: document.visibilityState === "visible",
    hidden_ms: Math.round(hiddenMs),
    failure_elapsed_ms: succeeded ? undefined : totalMs,
    health_poll_count: current.healthPollCount,
    outcome,
    failure_stage: failureStage,
    game: telemetryContext.game,
    game_version: telemetryContext.gameVersion,
    coworld_id: coworldId,
    episode_id: telemetryContext.episodeId,
    replay_delivery: current.delivery,
    session_shared: current.sessionShared,
    measurement_kind: measurementKind,
    surface,
    frontend_version: process.env.NEXT_PUBLIC_GIT_COMMIT,
  };
}
